import type {
  HomeAliadosContent,
  HomeAliadosLogo,
  HomeAliadosLogoBannerRow,
  HomeAliadosTextBannerRow,
} from '../models/home-aliados-content.model';
import { prepareAliadosTitleHtml } from './home-aliados-html.util';

function normalizeOrden(row: HomeAliadosTextBannerRow, index: number): number {
  const orden = row.orden;
  return typeof orden === 'number' && !Number.isNaN(orden) ? orden : index;
}

/**
 * Mapeo — registros `AliadosFinancieros` ordenados ASC:
 * - `orden` 0 → cabecera: `titulo` eyebrow, `nombreBoton` título HTML, `contenido` descripción
 * - `orden` 1 → CTA: `titulo` texto previo, `nombreBoton` frase link, `urlForward` URL
 */
export function mapAliadosTextBannersToContent(
  rows: HomeAliadosTextBannerRow[],
  fallback: HomeAliadosContent
): Pick<
  HomeAliadosContent,
  'eyebrow' | 'titleHtml' | 'description' | 'joinPrompt' | 'uniteLabel' | 'uniteUrl'
> {
  if (!rows?.length) {
    return {
      eyebrow: fallback.eyebrow,
      titleHtml: fallback.titleHtml,
      description: fallback.description,
      joinPrompt: fallback.joinPrompt,
      uniteLabel: fallback.uniteLabel,
      uniteUrl: fallback.uniteUrl,
    };
  }

  const sorted = [...rows].sort(
    (a, b) => normalizeOrden(a, 0) - normalizeOrden(b, 0)
  );

  const headerRow = sorted.find((row) => normalizeOrden(row, -1) === 0) ?? sorted[0];
  const ctaRow = sorted.find((row) => normalizeOrden(row, -1) === 1);

  const eyebrowRaw = headerRow?.titulo?.trim();
  const titleRaw = headerRow?.nombreBoton?.trim();
  const descriptionRaw = headerRow?.contenido?.trim();
  const joinPromptRaw = ctaRow?.titulo?.trim();
  const uniteLabelRaw = ctaRow?.nombreBoton?.trim();
  const uniteUrlRaw = ctaRow?.urlForward?.trim();

  return {
    eyebrow: eyebrowRaw || fallback.eyebrow,
    titleHtml: titleRaw ? prepareAliadosTitleHtml(titleRaw) : fallback.titleHtml,
    description: descriptionRaw || fallback.description,
    joinPrompt: joinPromptRaw || fallback.joinPrompt,
    uniteLabel: uniteLabelRaw || fallback.uniteLabel,
    uniteUrl: uniteUrlRaw || fallback.uniteUrl,
  };
}

/** Mapea logos ACTIVO de `Financiero` y ordena alfabéticamente por nombre. */
export function mapAliadosLogoBanners(
  rows: HomeAliadosLogoBannerRow[]
): HomeAliadosLogo[] {
  if (!rows?.length) {
    return [];
  }

  const mapped = rows
    .filter((row) => {
      const tipo = row.tipoArchivo?.trim();
      const image = row.urlArchivo?.trim();
      return (!tipo || tipo === 'Imagen') && !!image;
    })
    .map((row) => ({
      name: row.nombreArchivo?.trim() || 'Aliado financiero',
      image: row.urlArchivo!.trim(),
      href: row.urlForward?.trim() || '',
    }));

  return sortAliadosLogosAlphabetically(mapped);
}

/** Orden alfabético por nombre visible (locale es). */
export function sortAliadosLogosAlphabetically(
  logos: HomeAliadosLogo[]
): HomeAliadosLogo[] {
  return [...logos].sort((a, b) =>
    a.name.localeCompare(b.name, 'es', { sensitivity: 'base' })
  );
}

/**
 * Ordena alfabéticamente y divide en dos filas.
 * - Par: 50/50.
 * - Impar: fila 1 con un logo más.
 * No invierte ninguna fila (sin reverse).
 */
export function splitAliadosLogosIntoRows(logos: HomeAliadosLogo[]): {
  rowA: HomeAliadosLogo[];
  rowB: HomeAliadosLogo[];
} {
  if (!logos.length) {
    return { rowA: [], rowB: [] };
  }

  const sorted = sortAliadosLogosAlphabetically(logos);
  const mid = Math.ceil(sorted.length / 2);
  return {
    rowA: sorted.slice(0, mid),
    rowB: sorted.slice(mid),
  };
}

/**
 * @deprecated El marquee duplica en plantilla con dos `.home-aliados__logos-set`
 * de igual ancho para que translateX(-50%) caiga en la costura (sin gap en el track).
 * Se mantiene por compatibilidad de tests.
 */
export function duplicateForMarquee(logos: HomeAliadosLogo[]): HomeAliadosLogo[] {
  if (!logos.length) {
    return [];
  }
  return [...logos, ...logos];
}
